import { differenceInCalendarDays, isValid, parseISO } from 'date-fns'

const CURRENCY_BY_COUNTRY: Record<string, string> = {
  RO: 'RON',
  GB: 'GBP',
  US: 'USD',
  RS: 'RSD',
}

const CURRENCIES_MISSING_FROM_ECB = ['RSD']

const MAX_OBSERVATION_AGE_DAYS = 14

export interface FrankfurterRate {
  date: string
  base: string
  quote: string
  rate: number
}

export function getCurrency(country: string | null): string | null {
  if (!country) {
    return null
  }
  return CURRENCY_BY_COUNTRY[country] ?? null
}

export function isPublishedByEcb(currency: string): boolean {
  return !CURRENCIES_MISSING_FROM_ECB.includes(currency)
}

export function getEcbSeriesKey(currency: string): string {
  return `D.${currency}.EUR..A`
}

function assertFresh(observationDate: string | undefined): void {
  if (!observationDate) {
    throw new Error('Missing observation date')
  }
  const parsed = parseISO(observationDate)
  if (!isValid(parsed)) {
    throw new Error(`Invalid observation date: ${observationDate}`)
  }
  if (differenceInCalendarDays(new Date(), parsed) > MAX_OBSERVATION_AGE_DAYS) {
    throw new Error(`Stale exchange rate: ${observationDate}`)
  }
}

function toUsableRate(value: number): number {
  const rounded = Number(value.toFixed(4))
  if (!Number.isFinite(rounded) || rounded <= 0) {
    throw new Error(`Unusable exchange rate: ${value}`)
  }
  return rounded
}

export function extractExchangeRate(responseCSV: string): number {
  const rows = responseCSV.replace(/\r\n/g, '\n').split('\n').filter(Boolean)
  if (rows.length < 2) {
    throw new Error('Unexpected CSV format')
  }

  let delimiter = ','
  if (!rows[0].includes(delimiter)) {
    delimiter = ';'
  }
  const headers = rows[0].split(delimiter)
  const row = rows[1].split(delimiter)
  const observationValueColumnIndex = headers.findIndex((headerName) => headerName === 'OBS_VALUE')
  if (observationValueColumnIndex === -1) {
    throw new Error('OBS_VALUE column not found')
  }

  const timePeriodColumnIndex = headers.findIndex((headerName) => headerName === 'TIME_PERIOD')
  assertFresh(timePeriodColumnIndex === -1 ? undefined : row[timePeriodColumnIndex])

  const rawValue = row[observationValueColumnIndex]
  if (!rawValue) {
    throw new Error('Invalid OBS_VALUE in ECB response')
  }

  return toUsableRate(Number(rawValue))
}

export function extractFrankfurterExchangeRate(body: FrankfurterRate[], currency: string): number {
  if (!Array.isArray(body)) {
    throw new Error('Unexpected Frankfurter response format')
  }

  const entry = body.find((item) => item.base === 'EUR' && item.quote === currency)
  if (!entry) {
    throw new Error(`No ${currency} rate in Frankfurter response`)
  }

  assertFresh(entry.date)
  return toUsableRate(Number(entry.rate))
}
